// Central design tokens for the "Mether Scheduler" premium redesign.
// Components should read from the theme (theme.palette.*, theme.tokens.*) instead
// of hardcoding hex values scattered across pages.

export const PALETTE = {
  // Navy rail
  navy: '#1E2A5E',
  navy2: '#233169',
  navyText: '#FFFFFF',
  navyMuted: 'rgba(255,255,255,0.55)',
  navyHover: 'rgba(255,255,255,0.08)',

  // Primary indigo-blue
  primary: '#3D5AFE',
  primaryDark: '#2F49E0',
  primaryHover: '#3350F0',
  primarySoft: '#EAEDFF',
  primarySoftRgba: 'rgba(61,90,254,0.10)',

  // Surfaces
  workspace: '#ECEEF7',
  secondaryNav: '#F7F8FC',
  surface: '#FFFFFF',
  inputFill: '#F2F4FA',

  // Text
  heading: '#1E2A44',
  body: '#4A5267',
  muted: '#8A93A8',

  // Lines
  divider: '#E6E8F0',
  cardBorder: '#EEF0F6',

  // Status
  success: '#2ECC71',
  warning: '#F5A623',
  error: '#FF4D4F',
  info: '#3D5AFE',
  draft: '#9AA1B4',
  needsApproval: '#8B5CF6',
};

// Dark-mode counterparts (sensible variant; light is primary design)
export const DARK_PALETTE = {
  workspace: '#0E1326',
  secondaryNav: '#141A30',
  surface: '#181F38',
  inputFill: '#1F2742',
  heading: '#F4F6FC',
  body: '#C2C9DC',
  muted: '#8089A3',
  divider: 'rgba(255,255,255,0.08)',
  cardBorder: 'rgba(255,255,255,0.06)',
};

// Project / account circle-avatar palette
export const PROJECT_COLORS = [
  '#6C5CE7', // purple
  '#3D5AFE', // blue
  '#E08A4E', // orange
  '#1FBF8F', // green
  '#E0556E', // red
  '#3B4BA0', // indigo
];

// Deterministic project color from an id/name
export function projectColor(seed) {
  const str = String(seed ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return PROJECT_COLORS[hash % PROJECT_COLORS.length];
}

export function initialsOf(name) {
  const parts = String(name || '?')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/*
 * A slightly larger, calmer curve than the original set (base 7→8, card
 * 10→14, hero 12→16). Small radii read as "dense admin tool"; the products
 * this redesign is aimed at (Linear, Stripe, Notion, Vercel) sit a size or
 * two up from that on every surface. Existing keys keep their names —
 * nothing that already reads `RADII.card` etc. needs to change.
 */
export const RADII = {
  base: 8,
  card: 14,
  hero: 16,
  pill: 999,
  input: 9,
};

export const SHADOWS = {
  card: '0 4px 24px rgba(26,35,80,0.06)',
  cardHover: '0 8px 30px rgba(26,35,80,0.10)',
  popover: '0 12px 40px rgba(26,35,80,0.16)',
  // A tight, low ambient shadow for anything that should sit just barely off
  // the page — an input on focus, a chip, a small floating control. The
  // existing three were all sized for cards; nothing in the set was right
  // for something small.
  soft: '0 1px 2px rgba(26,35,80,0.04), 0 2px 8px rgba(26,35,80,0.05)',
  // The focus ring every interactive control should share — a real halo
  // rather than the browser default, but restrained enough not to look like
  // a validation error.
  focusRing: '0 0 0 3px rgba(61,90,254,0.16)',
};

export default PALETTE;
